import { onBeforeUnmount, ref } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  createGallery,
  deleteGallery,
  listGalleries,
  reorderGalleries,
  updateGallery,
} from '@/api/galleries'
import type { GalleryPayload, OrganizerGallery, UpdateGalleryPayload } from '@/types/galleries'
import { useGalleryToast } from '@/composables/useGalleryToast'
import { parseGalleryApiError } from '@/utils/galleries/apiError'
import { moveById } from '@/utils/galleries/reorder'
import type { MoveTarget } from '@/utils/galleries/reorder'

export const REORDER_DEBOUNCE_MS = 500

export interface UseGalleries {
  galleries: Ref<OrganizerGallery[]>
  loading: Ref<boolean>
  error: Ref<boolean>
  saving: Ref<boolean>
  fieldErrors: Ref<Record<string, string[]>>
  fetch: () => Promise<void>
  create: (payload: GalleryPayload) => Promise<OrganizerGallery | null>
  update: (id: string, payload: UpdateGalleryPayload) => Promise<OrganizerGallery | null>
  remove: (id: string) => Promise<boolean>
  move: (id: string, target: MoveTarget) => void
  reorder: (ids: string[]) => void
  clearFieldErrors: () => void
}

export function useGalleries(eventSlug: string): UseGalleries {
  const { t } = useI18n()
  const toast = useGalleryToast()

  const galleries = ref<OrganizerGallery[]>([])
  const loading = ref(false)
  const error = ref(false)
  const saving = ref(false)
  const fieldErrors = ref<Record<string, string[]>>({})

  let reorderTimer: ReturnType<typeof setTimeout> | null = null
  let rollbackSnapshot: OrganizerGallery[] | null = null

  async function fetch(): Promise<void> {
    loading.value = true
    error.value = false
    try {
      galleries.value = await listGalleries(eventSlug)
    } catch {
      error.value = true
    } finally {
      loading.value = false
    }
  }

  function clearFieldErrors(): void {
    fieldErrors.value = {}
  }

  async function create(payload: GalleryPayload): Promise<OrganizerGallery | null> {
    saving.value = true
    fieldErrors.value = {}
    try {
      const gallery = await createGallery(eventSlug, payload)
      galleries.value = [...galleries.value, gallery]
      return gallery
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      fieldErrors.value = parsed.errors
      if (parsed.status !== 422) toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return null
    } finally {
      saving.value = false
    }
  }

  async function update(id: string, payload: UpdateGalleryPayload): Promise<OrganizerGallery | null> {
    saving.value = true
    fieldErrors.value = {}
    try {
      const gallery = await updateGallery(eventSlug, id, payload)
      galleries.value = galleries.value.map((g) => (g.id === id ? { ...g, ...gallery } : g))
      return gallery
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      fieldErrors.value = parsed.errors
      if (parsed.status !== 422) toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return null
    } finally {
      saving.value = false
    }
  }

  async function remove(id: string): Promise<boolean> {
    saving.value = true
    try {
      await deleteGallery(eventSlug, id)
      galleries.value = galleries.value.filter((g) => g.id !== id)
      return true
    } catch {
      toast.push(t('galleries.errors.delete_failed'), { kind: 'error' })
      return false
    } finally {
      saving.value = false
    }
  }

  async function flushReorder(): Promise<void> {
    reorderTimer = null
    const snapshot = rollbackSnapshot
    rollbackSnapshot = null
    try {
      const result = await reorderGalleries(eventSlug, galleries.value.map((g) => g.id))
      galleries.value = galleries.value.map((g) => {
        const fresh = result.find((r) => r.id === g.id)
        return fresh === undefined ? g : { ...g, position: fresh.position }
      })
    } catch (e) {
      if (snapshot !== null) galleries.value = snapshot
      const parsed = parseGalleryApiError(e)
      toast.push(
        parsed.status === 422 ? t('galleries.errors.reorder_stale') : t('galleries.errors.reorder_failed'),
        { kind: 'error' },
      )
      if (parsed.status === 422) void fetch()
    }
  }

  /** Optimistic reorder: the list changes at once and is sent after 500 ms of quiet. */
  function reorder(ids: string[]): void {
    if (rollbackSnapshot === null) rollbackSnapshot = [...galleries.value]
    const byId = new Map(galleries.value.map((g) => [g.id, g]))
    galleries.value = ids.flatMap((id) => {
      const gallery = byId.get(id)
      return gallery === undefined ? [] : [gallery]
    })
    if (reorderTimer !== null) clearTimeout(reorderTimer)
    reorderTimer = setTimeout(() => {
      void flushReorder()
    }, REORDER_DEBOUNCE_MS)
  }

  function move(id: string, target: MoveTarget): void {
    reorder(moveById(galleries.value, id, target).map((g) => g.id))
  }

  onBeforeUnmount(() => {
    if (reorderTimer !== null) {
      clearTimeout(reorderTimer)
      void flushReorder()
    }
  })

  return {
    galleries,
    loading,
    error,
    saving,
    fieldErrors,
    fetch,
    create,
    update,
    remove,
    move,
    reorder,
    clearFieldErrors,
  }
}
